"use client"
import * as React from "react"
import classNames from "classnames"
import { GoArrowUpRight, GoArrowDownRight } from "react-icons/go"

import {
  requestGoogleMaps,
  useReplayAddressOnMapsLoad,
} from "../app/hooks/use-google-maps"
import PoweredByGoogle from "../assets/images/powered-by-google.webp"
import {
  getAddressProperty,
  getCityName,
  getLocationData,
  extractCity,
  geocodePlaceByPlaceId,
} from "../lib/google-maps/geocode"
import type { GoogleAddressData } from "../lib/google-maps/geocode"

export type { GoogleAddressData } from "../lib/google-maps/geocode"

type Prediction = {
  placeId: string
  description: string
}

type PlaceInputProps = {
  value: GoogleAddressData | null
  onChange: (value: GoogleAddressData | null) => void
  onBlur?: () => void
  onFocus?: () => void
  mapsLoaded: boolean
  warning?: string
  error?: string
  disabled?: boolean
  placeholder?: string
  searchOptions?: {
    types: string[]
    componentRestrictions: {
      country: string
    }
  }
  required?: boolean
  addressFormatter?: (data: GoogleAddressData) => string
  className?: string
  clearOnInputChange?: boolean
  allowCityPrediction?: boolean
  testId?: string
  type?: "pickup" | "dropoff"
  id?: string
  name?: string
  showLabel?: boolean
  variant?: string
  compact?: boolean
  prefixWithIcon?: boolean
  classes?: string
}

const defaultSearchOptions = {
  types: [] as string[],
  componentRestrictions: { country: "us" },
}

const getInputVariantClasses = (variant?: string, compact?: boolean) => {
  if (variant === "outline") {
    return "border rounded-md h-9"
  }

  if (compact) {
    return "bg-gray50 h-10 border-b md:h-[56px] md:mt-4"
  }

  return "bg-gray50 h-[56px] border-b mt-4"
}

export const PlaceInput = /* @__PURE__ */ React.forwardRef<
  HTMLInputElement,
  PlaceInputProps
>(function PlaceInput(props: PlaceInputProps, ref) {
  const {
    mapsLoaded,
    allowCityPrediction = false,
    searchOptions = defaultSearchOptions,
    required,
    showLabel = true,
    prefixWithIcon = true,
    compact = false,
    classes: labelClasses,
  } = props

  const [address, setAddress] = React.useState("")
  const [predictions, setPredictions] = React.useState<Prediction[]>([])
  const [activeIndex, setActiveIndex] = React.useState(-1)
  const [checkOnBlur, setCheckOnBlur] = React.useState(false)
  const [isOpen, setIsOpen] = React.useState(false)

  const inputRef = React.useRef<HTMLInputElement | null>(null)
  const autocompleteServiceRef = React.useRef<google.maps.places.AutocompleteService | null>(
    null,
  )
  const predictionsRef = React.useRef<Prediction[]>([])
  const addressRef = React.useRef("")
  const pendingBlurValidationRef = React.useRef(false)
  const blurValidationRequestRef = React.useRef(0)
  const wasMapsLoadedRef = React.useRef(mapsLoaded)
  const debounceTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null,
  )

  // Sync predictions ref
  predictionsRef.current = predictions
  addressRef.current = address

  // Sync address from external value changes
  React.useEffect(() => {
    if (props.value && props.value.formatted_address) {
      setAddress(props.value.formatted_address)
      addressRef.current = props.value.formatted_address
    } else {
      setAddress("")
      addressRef.current = ""
    }
  }, [props.value])

  // Init AutocompleteService when maps loads
  React.useEffect(() => {
    if (mapsLoaded && !autocompleteServiceRef.current) {
      autocompleteServiceRef.current = new google.maps.places.AutocompleteService()
    }
  }, [mapsLoaded])

  const fetchPredictions = React.useCallback(
    (
      input: string,
      options: { showDropdown?: boolean } = {},
    ): Promise<Prediction[] | null> => {
      const { showDropdown = true } = options

      if (!autocompleteServiceRef.current || !input.trim()) {
        setPredictions([])
        setIsOpen(false)

        return Promise.resolve(autocompleteServiceRef.current ? [] : null)
      }

      return new Promise((resolve) => {
        autocompleteServiceRef.current?.getPlacePredictions(
          {
            input,
            types: searchOptions.types,
            componentRestrictions: searchOptions.componentRestrictions,
          },
          (results, status) => {
            if (
              status === google.maps.places.PlacesServiceStatus.OK &&
              results
            ) {
              const mapped = results.map((r) => ({
                placeId: r.place_id,
                description: r.description,
              }))

              predictionsRef.current = mapped
              setPredictions(mapped)
              setActiveIndex(0)
              setIsOpen(showDropdown && mapped.length > 0)
              resolve(mapped)
            } else {
              predictionsRef.current = []
              setPredictions([])
              setIsOpen(false)
              resolve([])
            }
          },
        )
      })
    },
    [searchOptions],
  )

  useReplayAddressOnMapsLoad(mapsLoaded, inputRef, {
    suggest: fetchPredictions,
  })

  const clearDebouncedPredictions = () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
      debounceTimerRef.current = null
    }
  }

  const validateBlurPredictions = React.useCallback(
    (input: string, currentPredictions: Prediction[], requestId: number) => {
      if (
        requestId !== blurValidationRequestRef.current ||
        input !== addressRef.current
      ) {
        return
      }

      pendingBlurValidationRef.current = false
      const matched = currentPredictions.find(
        (p) => p.description.toLowerCase() === input.toLowerCase(),
      )

      if (matched) {
        selectPrediction(matched.description, matched.placeId)
      } else {
        props.onChange(null)
        setAddress("")
        addressRef.current = ""
      }
    },
    [props],
  )

  React.useEffect(() => {
    if (
      !wasMapsLoadedRef.current &&
      mapsLoaded &&
      pendingBlurValidationRef.current &&
      addressRef.current.trim()
    ) {
      const input = addressRef.current
      const requestId = blurValidationRequestRef.current

      fetchPredictions(input, { showDropdown: false }).then((results) => {
        if (results) {
          validateBlurPredictions(input, results, requestId)
        }
      })
    }

    wasMapsLoadedRef.current = mapsLoaded
  }, [fetchPredictions, mapsLoaded, validateBlurPredictions])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value

    blurValidationRequestRef.current += 1
    pendingBlurValidationRef.current = false
    setAddress(value)
    addressRef.current = value

    if (!checkOnBlur) {
      setCheckOnBlur(true)
    }

    // Debounce predictions
    clearDebouncedPredictions()

    if (mapsLoaded) {
      debounceTimerRef.current = setTimeout(() => {
        fetchPredictions(value)
      }, 300)
    }
  }

  const selectPrediction = async (description: string, placeId: string) => {
    clearDebouncedPredictions()

    // Verify the address matches a suggestion
    if (
      predictionsRef.current.length > 0 &&
      !predictionsRef.current.some(
        (p) => p.description.toLowerCase() === description.toLowerCase(),
      )
    ) {
      return
    }

    try {
      const { coordinates } = await geocodePlaceByPlaceId(placeId)
      const geocoder = new google.maps.Geocoder()
      const placeIdResults = await new Promise<google.maps.GeocoderResult[]>(
        (resolve, reject) => {
          geocoder.geocode({ placeId }, (results, status) => {
            if (status === "OK" && results) {
              resolve(results)
            } else {
              reject(status)
            }
          })
        },
      )

      const locationData = await getLocationData(coordinates, placeIdResults)

      const cityName = getCityName(locationData.address_components)
      const stateShortName = getAddressProperty(
        locationData.address_components,
        "administrative_area_level_1",
        "short_name",
      )
      const zipCode = getAddressProperty(
        locationData.address_components,
        "postal_code",
        "long_name",
      )

      const routeData: GoogleAddressData = {
        formatted_address: description,
        city: allowCityPrediction
          ? extractCity(cityName, description)
          : cityName || "",
        state_code: stateShortName || "",
        lat: coordinates.lat,
        lng: coordinates.lng,
        zip: zipCode,
      }

      if (props.addressFormatter) {
        routeData.formatted_address = props.addressFormatter(routeData)
      }

      if (props.clearOnInputChange) {
        setAddress("")
      } else {
        setAddress(routeData.formatted_address)
      }

      props.onChange(routeData)
      setCheckOnBlur(false)
      setPredictions([])
      setIsOpen(false)
    } catch {
      props.onChange(null)
    }
  }

  const handleBlur = () => {
    clearDebouncedPredictions()

    // Delay to allow click on suggestion to fire
    setTimeout(() => {
      if (checkOnBlur) {
        const currentAddress = addressRef.current

        if (
          currentAddress.trim() &&
          (!mapsLoaded || !autocompleteServiceRef.current)
        ) {
          pendingBlurValidationRef.current = true
          blurValidationRequestRef.current += 1
          setPredictions([])
          setIsOpen(false)
          props.onBlur?.()

          return
        }

        const currentPredictions = predictionsRef.current
        const matched = currentPredictions.find(
          (p) => p.description.toLowerCase() === currentAddress.toLowerCase(),
        )

        if (matched) {
          selectPrediction(matched.description, matched.placeId)
        } else if (currentPredictions.length > 0) {
          props.onChange(null)
          setAddress("")
          addressRef.current = ""
        } else if (currentAddress.trim()) {
          const requestId = blurValidationRequestRef.current + 1

          blurValidationRequestRef.current = requestId
          fetchPredictions(currentAddress, { showDropdown: false }).then(
            (results) => {
              if (results) {
                validateBlurPredictions(currentAddress, results, requestId)
              }
            },
          )
        } else {
          props.onChange(null)
          setAddress("")
          addressRef.current = ""
        }
      }

      setPredictions([])
      setIsOpen(false)
      props.onBlur?.()
    }, 200)
  }

  const handleFocus = () => {
    requestGoogleMaps()
    // Back in the field: the load replay shows suggestions instead.
    pendingBlurValidationRef.current = false

    if (!checkOnBlur && !props.value) {
      setCheckOnBlur(true)
    }

    props.onFocus?.()
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || predictions.length === 0) {
      return
    }

    if (e.key === "ArrowDown") {
      e.preventDefault()
      setActiveIndex((prev) => (prev < predictions.length - 1 ? prev + 1 : 0))
    } else if (e.key === "ArrowUp") {
      e.preventDefault()
      setActiveIndex((prev) => (prev > 0 ? prev - 1 : predictions.length - 1))
    } else if (e.key === "Enter") {
      e.preventDefault()

      if (activeIndex >= 0 && activeIndex < predictions.length) {
        const prediction = predictions[activeIndex]

        setAddress(prediction.description)
        selectPrediction(prediction.description, prediction.placeId)
      }
    } else if (e.key === "Escape") {
      setPredictions([])
      setIsOpen(false)
    }
  }

  const handleSuggestionMouseDown = (
    e: React.MouseEvent<HTMLLIElement>,
    prediction: Prediction,
  ) => {
    e.preventDefault()
    setAddress(prediction.description)
    selectPrediction(prediction.description, prediction.placeId)
  }

  // Compose refs
  const setRefs = React.useCallback(
    (node: HTMLInputElement | null) => {
      inputRef.current = node

      if (typeof ref === "function") {
        ref(node)
      } else if (ref) {
        ;(ref as React.MutableRefObject<HTMLInputElement | null>).current = node
      }
    },
    [ref],
  )

  return (
    <div>
      <div className="relative">
        {showLabel && (
          <label
            className={classNames(
              "flex uppercase text-gray16 text-base",
              props.error != null && "text-red-500",
              labelClasses,
            )}
            htmlFor={props.id}
          >
            {prefixWithIcon && (
              <span className="pr-1">
                {props.type === "pickup" ? (
                  <GoArrowUpRight size={24} className="text-gold" />
                ) : (
                  <GoArrowDownRight size={24} className="text-gold" />
                )}
              </span>
            )}
            {props.placeholder}
            {required && <span className="text-gray16">*</span>}
          </label>
        )}
        <input
          className={classNames(
            "w-full focus:outline-none text-sm lg:text-base xl:min-w-[272px]",
            compact ? "px-3 py-2 md:p-4" : "p-4",
            props.error != null
              ? "border-red-500 text-red-500"
              : "text-neutral-600 border-black",
            getInputVariantClasses(props.variant, compact),
          )}
          ref={setRefs}
          value={address}
          onChange={handleInputChange}
          onBlur={handleBlur}
          onFocus={handleFocus}
          onPointerDown={requestGoogleMaps}
          onKeyDown={handleKeyDown}
          disabled={props.disabled}
          id={props.id}
          data-testid={props.testId}
          placeholder={!showLabel ? props.placeholder : ""}
          aria-label={props.placeholder}
          autoComplete="off"
          role="combobox"
          aria-expanded={isOpen}
          aria-controls={`${props.name}-listbox`}
          aria-autocomplete="list"
        />
        <ul
          id={`${props.name}-listbox`}
          role="listbox"
          className={classNames("pb-0 mb-0", {
            "absolute left-0 top-full w-full border border-neutral-700 bg-white z-20 px-0":
              predictions.length > 0,
          })}
        >
          {predictions.map((prediction, index) => (
            <li
              className={classNames(
                "px-4 py-2.5 text-base cursor-pointer",
                index === activeIndex && "bg-primary-500 text-white",
              )}
              key={prediction.placeId}
              onMouseDown={(e) => handleSuggestionMouseDown(e, prediction)}
              onMouseEnter={() => setActiveIndex(index)}
              role="option"
              aria-selected={index === activeIndex}
            >
              <span>{prediction.description}</span>
            </li>
          ))}
          {predictions.length > 0 && (
            <li className={classNames("py-2 text-right")}>
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={PoweredByGoogle.src}
                alt="powered by google"
                className={classNames("inline-block w-28 h-auto")}
              />
            </li>
          )}
        </ul>
      </div>
      {props.warning && !props.error && (
        <p
          className={classNames(
            "inline-block text-base leading-none text-secondary-500",
          )}
        >
          {props.warning}
        </p>
      )}
      {props.error != null && (
        <span className="text-red-500 text-sm block">{props.error}</span>
      )}
    </div>
  )
})
